import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { usePortfolioDetailQuery } from '@/entities/portfolio';
import { PORTFOLIO_PATH } from '@/shared/config';
import { Button, buttonVariants, PageHeading } from '@/shared/ui';
import { PortfolioCompanyInfo } from './portfolio-company-info';
import { PortfolioCta } from './portfolio-cta';
import { PortfolioDetailHeader } from './portfolio-detail-header';
import { PortfolioDetailSkeleton } from './portfolio-detail-skeleton';
import { PortfolioGallery } from './portfolio-gallery';
import { PortfolioProducts } from './portfolio-products';
import { PortfolioRelated } from './portfolio-related';

interface PortfolioDetailProps {
  readonly id: string;
  readonly validId: boolean;
}

/**
 * 포트폴리오 사례 상세 — 머리(제목·고객사·메타 줄), 전폭 큰 사진과 썸네일, 회사소개·제작배경,
 * 포함 상품, 다른 사례, 제작 유도 띠 순서. 미공개·잘못된 주소는 같은 404 화면이다.
 */
export const PortfolioDetail = ({ id, validId }: PortfolioDetailProps): ReactElement => {
  const query = usePortfolioDetailQuery(id, { enabled: validId });
  const code = query.error?.data?.code;
  const notFound = !validId || code === 'NOT_FOUND' || code === 'BAD_REQUEST';
  const retry = () => {
    void query.refetch();
  };
  if (notFound)
    return (
      <section className="flex flex-col items-center gap-6 py-24 text-center">
        <PageHeading className="items-center text-center" title="페이지를 찾을 수 없습니다." />
        <Link to={PORTFOLIO_PATH} className={buttonVariants({ variant: 'outline', size: 'lg' })}>
          포트폴리오 목록으로
        </Link>
      </section>
    );
  if (query.isPending) return <PortfolioDetailSkeleton />;
  return (
    <>
      {query.isError && (
        // 원문 오류는 화면에 싣지 않는다 — 사용자용 문장과 재시도만 남긴다.
        <div
          role="alert"
          className="mb-10 flex flex-wrap items-center justify-between gap-4 border-y border-foreground/10 py-5"
        >
          <p className="text-sm text-destructive">포트폴리오를 불러오지 못했습니다.</p>
          <Button variant="outline" size="sm" onClick={retry}>
            다시 시도
          </Button>
        </div>
      )}
      {query.data !== undefined && (
        <div className="flex flex-col gap-24 sm:gap-32">
          <div className="flex flex-col gap-10 sm:gap-14">
            <PortfolioDetailHeader detail={query.data} onRetry={retry} />
            <PortfolioGallery
              key={query.data.id}
              portfolioId={query.data.id}
              title={query.data.title}
              images={query.data.images}
              onRetry={retry}
            />
            <PortfolioCompanyInfo detail={query.data} />
          </div>
          <PortfolioProducts products={query.data.products} onRetry={retry} />
          <PortfolioRelated detail={query.data} />
          <PortfolioCta />
        </div>
      )}
    </>
  );
};
